package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.statemachine.UiStateMachine
import top.wcpe.allincore.ui.runtime.animation.AnimationScheduler
import top.wcpe.allincore.ui.runtime.animation.StateMachineInstance
import top.wcpe.allincore.ui.runtime.animation.StateTransitionPayload
import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.DragReorder
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.InfiniteScroll
import top.wcpe.allincore.ui.runtime.element.Modal
import top.wcpe.allincore.ui.runtime.element.PullToRefresh
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.event.UiEventPredicate
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.debug.uiDebugOnce
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.call.CallDispatcher
import top.wcpe.allincore.ui.runtime.ir.call.LambdaValue
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 事件绑定分发器：负责将 DSL 声明的事件绑定到 UIElement 上。
 *
 * 从 IrInterpreter 中提取，职责单一化。
 *
 * RFC-INPUT-EVENT-UNIFICATION Stage 2：事件 lambda 形参可访问 event 字段。
 * 命名 action 路径（`onClick = saveFn()`）保持现有 0 参语义不变；
 * callable 路径（`onClick = ::fn` / lambda 字面量）在调用 LambdaValue 时
 * 把 MouseEvent / ChangeEvent 转 Map 注入第 1 实参。
 */
@Suppress(
    "TooManyFunctions",
    "CyclomaticComplexMethod",
    "LongMethod",
    "NestedBlockDepth",
    "ComplexCondition",
    "TooGenericExceptionCaught",
    "SwallowedException",
)
object EventBindingDispatcher {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.EventBindingDispatcher")

    /**
     * 将事件绑定到元素上。根据事件类型（onClick/onChange/onRightClick 等）分发到具体控件的事件方法。
     */
    @JvmStatic
    @JvmName("bindEvent")
    internal fun bindEvent(el: UIElement<*>, key: String, actionName: String, ctx: IrInterpreter.Context) {
        // 公共触发逻辑：先上报 dispatcher，再本地执行 action body。
        // 命名 action 路径下 action body 是 0 参 stmt 列表，不消费 event；
        // 仍把 trigger 设计为接受 event Map 形参以便统一接线，dslTask 内部忽略它。
        val dslTask = UiTask {
            try {
                ctx.dispatcher.send(actionName)
                val def = ctx.actionDefs[actionName]
                if (def != null && def.kind == UiAction.Kind.STATE_TRANSITION) {
                    dispatchStateTransition(el, def, ctx)
                    return@UiTask
                }
                val body = ctx.actions[actionName]
                if (!body.isNullOrEmpty()) {
                    ExprEvaluator.execute(body, ctx.evalCtx)
                }
            } catch (e: Exception) {
                LOGGER.error("[ui] 事件 '$key' 执行 action '$actionName' 时出错（已拦截，不影响客户端）", e)
            }
        }
        // 动画在主线程播放，DSL 逻辑提交到 DSL 执行线程
        val executor = ctx.evalCtx.executor
        val trigger = UiEventHandler<Map<String, Any?>> {
            playEventAnimations(el, key)
            if (executor != null) {
                executor.submit(dslTask)
            } else {
                dslTask.run()
            }
        }
        when (key) {
            "onClick" -> el.onClick { e ->
                if (el is Button && !el.enabled()) return@onClick
                trigger.accept(IrInterpreter.mouseEventToMap(e))
            }

            "onChange" -> bindOnChange(el, trigger, ctx)

            "onSubmit" -> if (el is TextField) {
                el.onSubmit { trigger.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onSubmit")
            }

            "onRightClick" -> if (el is Slot) {
                el.onRightClick(actionName)
                el.onRightClickHandler { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                warnUnsupportedEvent(el, "onRightClick")
            }

            "onLongPress" -> if (el is Slot) {
                el.onLongPress(actionName)
                el.onLongPressHandler { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onLongPress { e ->
                    if (el is Button && !el.enabled()) return@onLongPress
                    trigger.accept(IrInterpreter.mouseEventToMap(e))
                }
            }

            "onDoubleClick" -> if (el is Button) {
                el.onDoubleClick(actionName)
                el.onDoubleClickHandler { e ->
                    if (!el.enabled()) return@onDoubleClickHandler
                    trigger.accept(IrInterpreter.mouseEventToMap(e))
                }
            } else {
                warnUnsupportedEvent(el, "onDoubleClick")
            }

            "onHover" -> if (el is Slot) {
                el.onHover(actionName)
                el.onHoverHandler { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                warnUnsupportedEvent(el, "onHover")
            }

            "onDragEnter" -> if (el is Slot) {
                el.onDragEnter(actionName)
                el.onDragEnterHandler { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                warnUnsupportedEvent(el, "onDragEnter")
            }

            "onDragLeave" -> if (el is Slot) {
                el.onDragLeave(actionName)
                el.onDragLeaveHandler { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                warnUnsupportedEvent(el, "onDragLeave")
            }

            "interceptClick" -> if (el is Slot) {
                el.interceptClick(actionName)
                el.interceptClickHandler(buildInterceptPredicate(el, key, actionName, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptClick")
            }

            "interceptRightClick" -> if (el is Slot) {
                el.interceptRightClick(actionName)
                el.interceptRightClickHandler(buildInterceptPredicate(el, key, actionName, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptRightClick")
            }

            "interceptDrop" -> if (el is Slot) {
                el.interceptDrop(actionName)
                el.interceptDropHandler(buildInterceptPredicate(el, key, actionName, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptDrop")
            }

            "onDismiss" -> if (el is Modal) {
                el.onDismissActionName(actionName)
                el.onClick { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                warnUnsupportedEvent(el, "onDismiss")
            }

            "onRefresh" -> if (el is PullToRefresh) {
                el.onRefresh { trigger.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onRefresh")
            }

            "onLoadMore" -> if (el is InfiniteScroll) {
                el.onLoadMore { trigger.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onLoadMore")
            }

            "onReorder" -> if (el is DragReorder) {
                el.onReorder { from, to -> trigger.accept(reorderEventToMap(from, to)) }
            } else {
                warnUnsupportedEvent(el, "onReorder")
            }

            "onDragStart" -> el.onDragStart { trigger.accept(emptyMap()) }
            "onDragOver" -> el.onDragOver { trigger.accept(emptyMap()) }
            "onDrop" -> el.onDrop { trigger.accept(emptyMap()) }

            // v4 C.2：通用拖拽 / 指针移动 / 滚轮事件 prop（命名 action 路径，::handler 纯引用）
            "onDrag" -> el.onDrag { e -> trigger.accept(IrInterpreter.dragEventToMap(e)) }
            "onDragEnd" -> el.onDragEnd { e -> trigger.accept(IrInterpreter.dragEventToMap(e)) }
            "onPointerMove" -> el.onPointerMove { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
            // onWheel 是 onScroll 的 DSL 别名：DSL 写 onWheel，runtime 落到既有 onScroll handler
            "onWheel" -> el.onScroll { e -> trigger.accept(IrInterpreter.scrollEventToMap(e)) }

            else ->
                // 未识别事件 key 属前端可避免的高频告警，详细诊断时再打（按 key 去重）
                uiDebugOnce("unsupported-event", key) {
                    LOGGER.debug("[ui] 暂不支持事件: $key（已忽略）")
                }
        }
    }

    /**
     * onChange 在不同元素上是不同 ChangeEvent 类型的回调；统一把 ChangeEvent 转 Map
     * 后送 trigger，避免命名 action 路径下漏接事件。
     */
    private fun bindOnChange(el: UIElement<*>, trigger: UiEventHandler<Map<String, Any?>>, ctx: IrInterpreter.Context) {
        when {
            el is Checkbox -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            el is Switch -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            // Slider 的 onChange 需要走 SliderBindingHandler 协调状态机；这里仍用 UiTask 桥接。
            el is Slider -> ctx.sliderBindings.bindChangeTrigger(el) { trigger.accept(emptyMap()) }
            el is TextField -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            el is RadioGroup -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            el is Dropdown -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            el is TabView -> el.onChange { ev -> trigger.accept(IrInterpreter.changeEventToMap(ev)) }
            else -> warnUnsupportedEvent(el, "onChange")
        }
    }

    /**
     * EXPR 路径的 onChange 绑定：与 [bindOnChange] 对称——支持 onChange 的具体控件
     * 绑到自身字段（Switch/Slider/Checkbox/...），其它元素回退到 UIElement.onChangeGeneric
     * 兜底字段（保持旧 EXPR 测试 ExprEventBindingTest 对 box.onChange 的支持）。
     */
    private fun bindOnChangeCallable(
        el: UIElement<*>,
        trigger: UiEventHandler<Map<String, Any?>>,
        ctx: IrInterpreter.Context,
    ) {
        if (el is Checkbox ||
            el is Switch ||
            el is Slider ||
            el is TextField ||
            el is RadioGroup ||
            el is Dropdown ||
            el is TabView
        ) {
            bindOnChange(el, trigger, ctx)
        } else {
            // 通用兜底：onChangeGeneric 接收 MouseEvent（与原 EXPR 路径一致），转 Map 后下发
            el.onChangeGeneric { e -> trigger.accept(IrInterpreter.mouseEventToMap(e)) }
        }
    }

    /**
     * 把可调用对象注册为元素的事件 handler（与 bindEvent 走同一 trigger 路径）。
     *
     * RFC-INPUT-EVENT-UNIFICATION Stage 2：每次事件触发时把 event 转 Map，
     * 通过 [invokeEventCallable] 注入到 callable 的第 1 实参。1 参 lambda
     * 体内可读 `event.button` 等字段；0 参 fun 引用 / 部分应用按 0 参调用，
     * 实参 Map 被 [shouldPassEvent] 识别后丢弃。
     */
    @JvmStatic
    @JvmName("installExprEventHandler")
    internal fun installExprEventHandler(el: UIElement<*>, key: String, callable: Any?, ctx: IrInterpreter.Context) {
        val executor = ctx.evalCtx.executor
        // wrapped 接受事件 Map：先播动画（同步），再把回调提交给 DSL 执行线程
        val wrapped = UiEventHandler<Map<String, Any?>> { eventMap ->
            playEventAnimations(el, key)
            val invoke = UiTask { invokeEventCallable(callable, eventMap, ctx, key) }
            if (executor != null) {
                executor.submit(invoke)
            } else {
                invoke.run()
            }
        }
        when (key) {
            "onClick" -> el.onClick { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }

            "onRightClick" -> if (el is Slot) {
                // Slot 上 onRightClickHandler() 覆盖了 UIElement 版本，DSL 端绑定必须写到 Slot 自己的字段；
                // 否则 InputDispatcher 通过多态读到 Slot 字段为 null，事件无法触发。
                el.onRightClickHandler { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onRightClick { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            }

            "onHover" -> if (el is Slot) {
                // Slot 覆盖了 onHoverHandler() getter，DSL 端绑定必须写到 Slot 自己的字段；
                // 否则 InputDispatcher 通过多态读到 Slot 字段为 null，事件无法触发（对齐上方 onRightClick）。
                el.onHoverHandler { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onHover { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            }

            "onLongPress" -> if (el is Slot) {
                el.onLongPressHandler { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onLongPress { e ->
                    if (el is Button && !el.enabled()) return@onLongPress
                    wrapped.accept(IrInterpreter.mouseEventToMap(e))
                }
            }

            "onDoubleClick" -> if (el is Button) {
                el.onDoubleClickHandler { e ->
                    if (!el.enabled()) return@onDoubleClickHandler
                    wrapped.accept(IrInterpreter.mouseEventToMap(e))
                }
            } else {
                warnUnsupportedEvent(el, "onDoubleClick")
            }

            "onDragEnter" -> if (el is Slot) {
                // 同 onHover：Slot 覆盖了 onDragEnterHandler() getter，须写到 Slot 自己的字段。
                el.onDragEnterHandler { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onDragEnter { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            }

            "onDragLeave" -> if (el is Slot) {
                // 同 onHover：Slot 覆盖了 onDragLeaveHandler() getter，须写到 Slot 自己的字段。
                el.onDragLeaveHandler { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            } else {
                el.onDragLeave { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            }

            "onChange" -> bindOnChangeCallable(el, wrapped, ctx)

            "onSubmit" -> if (el is TextField) {
                el.onSubmit { wrapped.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onSubmit")
            }

            "onDismiss" -> if (el is Modal) {
                el.onClick { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            }

            "onRefresh" -> if (el is PullToRefresh) {
                el.onRefresh { wrapped.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onRefresh")
            }

            "onLoadMore" -> if (el is InfiniteScroll) {
                el.onLoadMore { wrapped.accept(emptyMap()) }
            } else {
                warnUnsupportedEvent(el, "onLoadMore")
            }

            "onReorder" -> if (el is DragReorder) {
                el.onReorder { from, to -> wrapped.accept(reorderEventToMap(from, to)) }
            } else {
                warnUnsupportedEvent(el, "onReorder")
            }

            "onDragStart" -> el.onDragStart { wrapped.accept(emptyMap()) }
            "onDragOver" -> el.onDragOver { wrapped.accept(emptyMap()) }
            "onDrop" -> el.onDrop { wrapped.accept(emptyMap()) }

            "interceptClick" -> if (el is Slot) {
                el.interceptClickHandler(buildExprInterceptPredicate(el, key, callable, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptClick")
            }

            "interceptRightClick" -> if (el is Slot) {
                el.interceptRightClickHandler(buildExprInterceptPredicate(el, key, callable, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptRightClick")
            }

            "interceptDrop" -> if (el is Slot) {
                el.interceptDropHandler(buildExprInterceptPredicate(el, key, callable, ctx))
            } else {
                warnUnsupportedEvent(el, "interceptDrop")
            }

            // v4 C.2：通用拖拽 / 指针移动 / 滚轮事件 prop（EXPR 路径，::handler(args) / lambda）
            "onDrag" -> el.onDrag { e -> wrapped.accept(IrInterpreter.dragEventToMap(e)) }
            "onDragEnd" -> el.onDragEnd { e -> wrapped.accept(IrInterpreter.dragEventToMap(e)) }
            "onPointerMove" -> el.onPointerMove { e -> wrapped.accept(IrInterpreter.mouseEventToMap(e)) }
            // onWheel 是 onScroll 的 DSL 别名：runtime 仍落到既有 onScroll handler
            "onWheel" -> el.onScroll { e -> wrapped.accept(IrInterpreter.scrollEventToMap(e)) }

            else ->
                // 未识别的事件 key 一律告警而不再无脑挂到 onClick 上：
                // 历史实现把任何漏写 case 的 key 错绑成普通点击 handler，
                // 一旦 isEventPropKey 收录新 key（如 intercept*）就会引发"看似生效却语义错位"的隐蔽 bug。
                // 前端可避免的高频告警，详细诊断时再打（按 key 去重）。
                uiDebugOnce("expr-unsupported-event", key) {
                    LOGGER.debug("[ui] EXPR 路径暂不支持事件: $key（已忽略，未挂任何 handler）")
                }
        }
    }

    private fun reorderEventToMap(fromIndex: Int, toIndex: Int): Map<String, Any?> {
        val event = LinkedHashMap<String, Any?>()
        event["fromIndex"] = fromIndex
        event["toIndex"] = toIndex
        return event
    }

    /**
     * 「元素不支持某事件」告警：属前端可避免的高频提示，仅在 UI 域调试开启时按
     * (元素类型, 事件名) 去重打 DEBUG，正常运行静默忽略。
     */
    private fun warnUnsupportedEvent(el: UIElement<*>, eventKey: String) {
        val elName = el::class.simpleName
        uiDebugOnce("unsupported-$eventKey", elName) {
            LOGGER.debug("[ui] 元素 $elName 不支持 $eventKey 事件（已忽略）")
        }
    }

    /**
     * 调用事件 callable 并按需注入 event Map。
     *
     * 策略（仅对事件入口生效，不影响普通 fun 调用约定）：
     * - callable 是 [LambdaValue] 且形参恰好 1 个 → 把 event Map 作为第 1 实参传入；
     * - 其它形态（FunRefValue / BoundFunRefValue / LocalFunValue / 0 参或多参 LambdaValue）
     *   → 按 0 参调用，与旧路径完全一致，避免实参检查抛错。
     */
    private fun invokeEventCallable(
        callable: Any?,
        eventMap: Map<String, Any?>,
        ctx: IrInterpreter.Context,
        key: String,
    ) {
        val args: List<Any?> = if (shouldPassEvent(callable)) listOf(eventMap) else emptyList()
        try {
            CallDispatcher.invokeCallable(callable, args, ctx.evalCtx, key)
        } catch (e: DslRuntimeError) {
            LOGGER.error("[ui] 事件 '$key' 执行失败，已中断当前事件并拦截异常：${e.message}")
        } catch (e: RuntimeException) {
            LOGGER.error("[ui] 事件 '$key' 执行出现未预期异常，已中断当前事件并拦截异常", e)
        }
    }

    /**
     * 是否应把 event Map 作为第 1 实参传入 callable。
     *
     * 仅 1 参 LambdaValue（含 suspend lambda 在事件位置非法但保守不区分）需要注入；
     * 其它可调用对象按 0 参调用，与 RFC §3.3 旧形式（`onClick = saveFn()`）兼容。
     */
    private fun shouldPassEvent(callable: Any?): Boolean {
        if (callable is LambdaValue) {
            return callable.params.size == 1
        }
        return false
    }

    /**
     * 为无事件绑定但有动画的元素安装触发器。
     */
    @JvmStatic
    @JvmName("installAnimationOnlyEventTriggers")
    internal fun installAnimationOnlyEventTriggers(
        el: UIElement<*>,
        props: Map<String, UiBinding>,
        ctx: IrInterpreter.Context,
    ) {
        if ((el.animationFor("onClickAnimation") != null || el.animationFor("click") != null) &&
            !hasEventProp(props, "onClick")
        ) {
            el.onClick {
                playAnimation(el, "onClickAnimation")
                playAnimation(el, "click")
            }
        }
        if ((el.animationFor("onChangeAnimation") != null || el.animationFor("change") != null) &&
            !hasEventProp(props, "onChange")
        ) {
            installAnimationOnlyChangeTrigger(el, ctx)
        }
    }

    /**
     * 播放事件关联的动画（同时尝试旧名和新名）。
     */
    @JvmStatic
    fun playEventAnimations(el: UIElement<*>, eventKey: String) {
        val oldTrigger = eventAnimationTrigger(eventKey)
        playAnimation(el, oldTrigger)
        when (eventKey) {
            "onClick" -> playAnimation(el, "click")
            "onChange" -> playAnimation(el, "change")
        }
    }

    // ===== 私有辅助方法 =====

    private fun hasEventProp(props: Map<String, UiBinding>, key: String): Boolean {
        val binding = props[key]
        return binding != null && binding.type == UiBinding.BindingType.EVENT
    }

    private fun installAnimationOnlyChangeTrigger(el: UIElement<*>, ctx: IrInterpreter.Context) {
        val trigger = UiTask {
            playAnimation(el, "onChangeAnimation")
            playAnimation(el, "change")
        }
        when {
            el is Checkbox -> el.onChange { trigger.run() }
            el is Switch -> el.onChange { trigger.run() }
            el is Slider -> ctx.sliderBindings.bindChangeTrigger(el, trigger)
            el is TextField -> el.onChange { trigger.run() }
            el is RadioGroup -> el.onChange { trigger.run() }
            el is Dropdown -> el.onChange { trigger.run() }
            el is TabView -> el.onChange { trigger.run() }
        }
    }

    private fun eventAnimationTrigger(key: String): String? = when (key) {
        "onClick" -> "onClickAnimation"
        "onChange" -> "onChangeAnimation"
        else -> null
    }

    @JvmStatic
    @JvmName("playAnimation")
    internal fun playAnimation(el: UIElement<*>, trigger: String?) {
        val scheduler = el.animationScheduler() as AnimationScheduler? // marker 断环，见 AnimationDriverHandle
        if (trigger == null || scheduler == null) {
            return
        }
        val handle = scheduler.play(el, trigger)
        if (handle != null) {
            val callback = el.animationCompleteCallbackFor(trigger)
            if (callback != null) {
                handle.onComplete(callback)
            }
        }
    }

    private fun buildInterceptPredicate(
        el: UIElement<*>,
        key: String,
        actionName: String,
        ctx: IrInterpreter.Context,
    ): UiEventPredicate<MouseEvent> = UiEventPredicate {
        playAnimation(el, eventAnimationTrigger(key))
        val body = ctx.actions[actionName]
        if (body.isNullOrEmpty()) {
            false
        } else {
            val result = ExprEvaluator.executeAndReturn(body, ctx.evalCtx)
            result == true
        }
    }

    /**
     * EXPR 路径的拦截器 UiEventPredicate 构造器：与 [buildInterceptPredicate] 对称。
     *
     * 命名 action 路径取 `ctx.actions.get(actionName)` 同步执行 body 拿返回值；
     * EXPR 路径下 `callable` 已是求值后的 LambdaValue / FunRefValue / BoundFunRefValue，
     * 通过 [CallDispatcher.invokeCallable] 直接调用并取其返回值。
     *
     * 语义约定：
     * - 返回 [true] → 拦截（短路 vanilla 发包 / observer）；
     * - 返回非 true / null / 抛异常 → 视为不拦截，与命名 action 路径完全对齐；
     * - 1 参 LambdaValue 自动注入 event Map（与观察事件入口一致）；其它形态按 0 参调用。
     */
    private fun buildExprInterceptPredicate(
        el: UIElement<*>,
        key: String,
        callable: Any?,
        ctx: IrInterpreter.Context,
    ): UiEventPredicate<MouseEvent> = UiEventPredicate { e ->
        playAnimation(el, eventAnimationTrigger(key))
        try {
            val eventMap = IrInterpreter.mouseEventToMap(e)
            val args: List<Any?> = if (shouldPassEvent(callable)) listOf(eventMap) else emptyList()
            val result = CallDispatcher.invokeCallable(callable, args, ctx.evalCtx, key)
            result == true
        } catch (ex: DslRuntimeError) {
            LOGGER.error("[ui] 拦截器 '$key' 执行失败，已视为不拦截：${ex.message}")
            false
        } catch (ex: RuntimeException) {
            LOGGER.error("[ui] 拦截器 '$key' 执行出现未预期异常，已视为不拦截", ex)
            false
        }
    }

    /**
     * 路由状态机转换。
     */
    private fun dispatchStateTransition(el: UIElement<*>, def: UiAction, ctx: IrInterpreter.Context) {
        val payload = StateTransitionPayload.decode(def.bodyBytes)
        val fsmDef: UiStateMachine = ctx.stateMachines[payload.fsmId]
            ?: throw IllegalStateException("STATE_TRANSITION 引用了未定义的状态机: ${payload.fsmId}")
        val byFsm = ctx.fsmInstances.getOrPut(el) { LinkedHashMap() }
        val inst = byFsm.getOrPut(payload.fsmId) {
            // marker 断环，见 AnimationDriverHandle
            StateMachineInstance(fsmDef, el, el.animationScheduler() as AnimationScheduler?, ctx.evalCtx)
        }
        inst.transitionTo(payload.target)
    }
}
